/**
 * 소싱관리 보드 신호 계산 — 진입점수 · 1페이지 내 위치 막대.
 *
 * 순수 계산만 한다. DOM·store·API 를 모르고, 행 객체를 고치지도 않는다.
 * 정본: docs/sourcing-board-redesign.md §3(지표) · §4(층 분리) · §7(데이터)
 *
 * ⚠ 이 파일은 「판정」을 하지 않는다.
 *   노출여력/이미포화 같은 기회 판정은 keywordDataCollect(1페이지 전체를 보는 층)가 하고,
 *   보드에는 상위 3행만 오므로 여기서 다시 계산할 수 없다(§4). 담기가 실어 보낸
 *   `상품_기회` 라벨을 읽기만 한다. 임계값이 두 층에 있으면 두 층이 갈린다.
 *
 * ⚠ 매입가·마진·ROI 계산은 여기 없다. `utils/purchaseCost.js` 가 정본이다. 복제 금지.
 */

/* ------------------------------------------------------------------ *
 * 잠정 임계값 — 전부 한 곳에 모은다
 *
 * 실데이터 없이 맞출 수 없는 숫자다. 코드 상수로 흩뿌리면 나중에 못 고친다.
 * 추후 조건서(`calc` 규칙, 정본 §5 "규칙의 세 종류")로 이관한다.
 * 이관 전까지는 이 블록 바깥에 숫자를 쓰지 말 것.
 * ------------------------------------------------------------------ */

/** `상품_기회` 라벨이 가질 수 있는 값 (정본 §3.2). 담기가 이 문자열 그대로 실어 보낸다. */
export const OPP = {
  GAP: '노출여력',
  SAT: '이미포화',
  WEAK: '전환약함',
  THIN: '표본부족',
  UNKNOWN: '노출미상'
}

/** 기회계수 — 진입점수의 첫째 축. (잠정) */
export const OPP_WEIGHT = {
  노출여력: 1.0,
  이미포화: 0.6,
  노출미상: 0.45,
  표본부족: 0.25,
  전환약함: 0.1
}

/** 라벨이 없는 행(= RPI 중간대, 노출은 알려짐)의 기회계수. (잠정) */
export const DEFAULT_OPP_WEIGHT = 0.45

/**
 * ROI 상한(%). (잠정)
 * 이 위는 대개 초저가 상품이거나 매입가가 잘못 들어간 값이라, 안 자르면
 * ROI 1200% 짜리 쓰레기가 1등이 된다(정본 §3.1).
 */
export const ROI_CAP = 300

/**
 * 시장파이 로그 기준(원). (잠정)
 * 세 축 스케일이 달라서(매출은 억 단위, ROI 는 배수, 기회는 0~1) 그대로 곱하면
 * 시장파이가 다 먹는다. 로그를 씌워야 1천만↔1억 차이가 1억↔10억 차이와 같은
 * 무게가 된다 — 안 그러면 큰 시장만 위로 올라오고 작지만 확실한 자리를 놓친다.
 */
export const MARKET_PIE_LOG_BASE = 1e6

/** 진입점수 표시 배율. 0~1 대역을 두 자리 수로 펴서 읽기 쉽게 한다. (잠정) */
export const SCORE_SCALE = 10

/**
 * 백분위 눈금 ↔ 분포 필드명. 낮은 백분위부터.
 * 실제로 오는 분포마다 눈금 수가 다르다(§3.3) — RPI 는 min/median/max 3개,
 * 가격은 IQR 2개뿐이다. 그래서 있는 눈금만 골라 쓴다.
 */
const ANCHOR_PCTS = [[0, 'min'], [25, 'q1'], [50, 'median'], [75, 'q3'], [100, 'max']]

/**
 * 1페이지 가격 분포가 담기는 행 필드명.
 *
 * `priceRange25to75`(노출수 상위 20개의 25~75% 구간)와 **다른 값**이다. 그쪽은
 * 비즈니스인사이트 구독이 있어야 나오는 노출수 가중 분포라 로켓그로스 보드에서는
 * 영영 안 들어온다. 이 필드는 노출 가중 없는 **1페이지 상품 가격의 min~max** 라
 * 일반 검색만으로 구해진다.
 *
 * ⚠ 같은 이름에 다른 의미를 넣지 않으려고 필드를 갈랐다. 덮어쓰면 판매/분석 보드가
 *   읽는 값이 조용히 다른 뜻으로 바뀐다.
 */
export const ONE_PAGE_PRICE_FIELD = '1p가격범위'

/* ------------------------------------------------------------------ */

/**
 * 숫자로 바꾼다. 못 바꾸면 0 이 아니라 null.
 * 「세어보지 않은 것」과 「0」은 다른 값이다(정본 §8-④).
 * @param {*} x
 * @returns {number|null}
 */
function toNum (x) {
  if (x === null || x === undefined || x === '') return null
  const n = parseFloat(x)
  return Number.isFinite(n) ? n : null
}

/**
 * 분포 안에서의 백분위(0~100).
 *
 * `dist` 에서 값이 있는 눈금만 모아 구간별 선형보간한다.
 *  - min/q1/median/q3/max 가 다 있으면 0-25-50-75-100 4구간
 *  - min/median/max 만 있으면 0-50-100 2구간 (RPI·노출)
 *  - q1/q3 만 있으면 25-75 1구간, 바깥은 눈금값으로 잘린다 (가격 IQR)
 *
 * min/median/max 3구간 경로는 keywordDataCollect 의
 * `percentileFromMinMedianMax()`(:6086)와 같은 값을 내야 한다 — 그 함수가
 * `상품_기회` 라벨을 정했으므로, 막대와 배지가 어긋나면 안 된다.
 *
 * @param {*} value 상품값
 * @param {{min?:*, q1?:*, median?:*, q3?:*, max?:*}} dist 분포 눈금
 * @returns {number|null} 0~100. 값이 없거나 눈금이 2개 미만이면 null
 */
export function pctIn (value, dist) {
  const v = toNum(value)
  if (v === null || !dist) return null

  const anchors = []
  for (const [pct, key] of ANCHOR_PCTS) {
    const n = toNum(dist[key])
    if (n !== null) anchors.push({ pct, value: n })
  }
  // 눈금이 하나뿐이면 「어디쯤인지」를 말할 수 없다. 0 으로 뭉개지 않는다.
  if (anchors.length < 2) return null

  // 눈금이 오름차순이 아니면(median < min 같은 깨진 분포) 계산을 포기한다.
  // 한쪽 구간만 멀쩡해서 그럴듯한 숫자가 나오는 게 제일 나쁘다 — 틀린 줄 모른다.
  for (let i = 1; i < anchors.length; i++) {
    if (anchors[i].value < anchors[i - 1].value) return null
  }

  const first = anchors[0]
  const last = anchors[anchors.length - 1]
  if (v <= first.value) return first.pct
  if (v >= last.value) return last.pct

  for (let i = 0; i < anchors.length - 1; i++) {
    const lo = anchors[i]
    const hi = anchors[i + 1]
    if (v > lo.value && v <= hi.value) {
      const span = hi.value - lo.value
      // 눈금 두 개가 같은 값으로 왔다(퇴화). 나누지 않고 위쪽 눈금을 준다.
      if (span <= 0) return hi.pct
      return lo.pct + ((v - lo.value) / span) * (hi.pct - lo.pct)
    }
  }

  // 위 클램프와 오름차순 검사를 지나면 반드시 어느 구간엔가 든다. 방어용.
  return null
}

/**
 * RPI 위치 — 1페이지 안에서 이 상품의 노출 1회당 기대매출이 어디쯤인가.
 *
 * 분포는 `minRpi/medianRpi/maxRpi` 3점뿐이다(사분위 없음, 정본 §3.3).
 * `topRankMinRpi/topRankMaxRpi` 는 상위 랭킹만의 분포라 눈금이 달라서
 * 백분위 계산에는 섞지 않고, 막대 위에 목표 구간을 그리라고 같이 넘긴다.
 *
 * @param {object} row 보드 행
 * @returns {{pct:number, dist:object}|null}
 */
export function rpiPosition (row) {
  if (!row) return null

  const dist = {
    min: row.minRpi,
    median: row.medianRpi,
    max: row.maxRpi,
    // 아래 둘은 pctIn 이 보지 않는다(눈금 키가 아니다). 화면 표시용.
    topRankMin: row.topRankMinRpi,
    topRankMax: row.topRankMaxRpi
  }

  // 상품 RPI 는 담기가 실어 보낸 값이 먼저. 없으면 키워드 평균으로 대신한다.
  const value = row.상품_calculatedRPI != null ? row.상품_calculatedRPI : row.avgRpi

  const pct = pctIn(value, dist)
  return pct === null ? null : { pct, dist }
}

/**
 * `priceRange25to75` 를 IQR 로 푼다.
 *
 * 실제로 오는 모양은 **문자열**이다 — `'12,000원 ~ 25,000원'`
 * (keywordDataCollect.vue:7373-7381, 노출수 상위 20개 상품의 25%/75% 가격).
 * 데이터가 없으면 `'0원 ~ 0원'` 이 온다. 다른 경로가 배열·객체로 보낼 수도 있어
 * 세 모양을 다 받는다.
 *
 * @param {*} raw
 * @returns {{q1:number, q3:number}|null}
 */
function parseIqr (raw) {
  if (raw === null || raw === undefined) return null

  let q1 = null
  let q3 = null

  if (Array.isArray(raw)) {
    q1 = toNum(raw[0])
    q3 = toNum(raw[1])
  } else if (typeof raw === 'object') {
    q1 = toNum(raw.q1 !== undefined ? raw.q1 : raw.min)
    q3 = toNum(raw.q3 !== undefined ? raw.q3 : raw.max)
  } else {
    // '12,000원 ~ 25,000원' — 쉼표·단위를 떼고 앞뒤 숫자만 본다.
    const parts = String(raw).split('~')
    if (parts.length < 2) return null
    const digits = (s) => {
      const d = s.replace(/[^0-9.]/g, '')
      return d === '' ? null : toNum(d)
    }
    q1 = digits(parts[0])
    q3 = digits(parts[1])
  }

  // '0원 ~ 0원' = 「가격을 못 구했다」는 뜻이지 「0원짜리 시장」이 아니다.
  // 이걸 그대로 쓰면 어떤 판매가든 상한 밖으로 나가 '상위 75%' 로 찍힌다.
  if (q1 === null || q3 === null || q1 <= 0 || q3 <= 0) return null
  return { q1, q3 }
}

/**
 * 1페이지 가격 분포(min~max)를 푼다.
 *
 * `parseIqr` 와 모양은 같지만 **눈금의 뜻이 다르다** — 이쪽은 사분위가 아니라 실제
 * 최저·최고가라 0~100 전 구간을 덮는다. 그래서 눈금 키도 q1/q3 가 아니라 min/max 로
 * 돌려준다(pctIn 이 이 키를 0%·100% 로 읽는다).
 *
 * 받는 모양 세 가지:
 *  - `{ min, max }` (조회 결과가 담기는 정식 모양)
 *  - `[min, max]`
 *  - `'8,900원 ~ 34,000원'`
 *
 * @param {*} raw
 * @returns {{min:number, max:number}|null}
 */
function parseMinMax (raw) {
  if (raw === null || raw === undefined) return null

  let min = null
  let max = null

  if (Array.isArray(raw)) {
    min = toNum(raw[0])
    max = toNum(raw[1])
  } else if (typeof raw === 'object') {
    min = toNum(raw.min)
    max = toNum(raw.max)
  } else {
    const parts = String(raw).split('~')
    if (parts.length < 2) return null
    const digits = (s) => {
      const d = s.replace(/[^0-9.]/g, '')
      return d === '' ? null : toNum(d)
    }
    min = digits(parts[0])
    max = digits(parts[1])
  }

  // 0 은 「가격을 못 구했다」지 「0원짜리 시장」이 아니다. parseIqr 와 같은 이유로 버린다.
  if (min === null || max === null || min <= 0 || max <= 0) return null
  // 뒤집힌 분포는 계산을 포기한다 — 한쪽만 멀쩡해서 그럴듯한 숫자가 나오는 게 제일 나쁘다.
  if (max < min) return null
  return { min, max }
}

/**
 * 가격 위치 — 1페이지 가격대 안에서 **우리가 팔 가격**이 어디쯤인가.
 *
 * ⚠ `벤치마킹판매가`(경쟁 상품 가격)가 아니라 `판매가`(우리 가격)다. 정본 §3.3 주의.
 *
 * 분포 출처가 보드마다 다르다. **같은 칸이지만 같은 자가 아니다.**
 *  - 기본(판매/분석 보드) = `priceRange25to75`. 노출수 상위 20개의 25~75% 구간이라
 *    비즈니스인사이트 구독이 있어야 실린다. 눈금이 q1/q3 둘뿐이라 바깥은 25/75 로
 *    잘린다 — 「IQR 아래냐 위냐」까지만 말할 수 있다.
 *  - `opts.onePage` (로켓그로스 보드) = `1p가격범위`. 노출 가중 없는 1페이지 상품
 *    가격의 min~max 라 구독 없이 구해지고, 0~100 전 구간을 덮는다.
 *
 * @param {object} row 보드 행
 * @param {{onePage?:boolean}} [opts] onePage=true 면 1페이지 min~max 분포를 읽는다
 * @returns {{pct:number, dist:object}|null}
 */
export function pricePosition (row, opts) {
  const dist = priceDist(row, opts)
  if (!dist) return null
  const pct = pctIn(row.판매가, dist)
  return pct === null ? null : { pct, dist }
}

/**
 * 가격 분포만 꺼낸다 — **판매가와 무관하다.**
 *
 * `pricePosition` 은 판매가가 없으면 null 이라, 「가격대는 아는데 얼마에 팔지 아직
 * 안 정한」 상태를 표현할 수 없다. 가격 다이얼(%→판매가)이 바로 그 상태에서 쓰인다.
 * 그래서 분포를 따로 꺼낼 길을 둔다. 출처 고르는 규칙은 `pricePosition` 과 같다.
 *
 * @param {object} row 보드 행
 * @param {{onePage?:boolean}} [opts]
 * @returns {{min:number,max:number}|{q1:number,q3:number}|null}
 */
export function priceDist (row, opts) {
  if (!row) return null
  return (opts && opts.onePage)
    ? parseMinMax(row[ONE_PAGE_PRICE_FIELD])
    : parseIqr(row.priceRange25to75)
}

/**
 * `pctIn` 의 역함수 — 분포 안에서 백분위 `pct` 자리의 **값**을 돌려준다.
 *
 * 가격 다이얼의 정방향(% → 판매가)이 이 하나로 끝난다. `pctIn` 과 같은 눈금·같은
 * 구간을 쓰므로 `valueAtPct(pctIn(v, d), d) === v` 가 (구간 안에서) 성립한다.
 *
 * 눈금이 2개 미만이거나 오름차순이 아니면 `pctIn` 과 똑같이 null 이다 — 한쪽만
 * 계산해 주면 다이얼과 막대가 서로 다른 말을 한다.
 *
 * @param {number} pct 0~100. 밖으로 나가면 양 끝으로 자른다
 * @param {{min?:*, q1?:*, median?:*, q3?:*, max?:*}} dist 분포 눈금
 * @returns {number|null}
 */
export function valueAtPct (pct, dist) {
  const p = toNum(pct)
  if (p === null || !dist) return null

  const anchors = []
  for (const [a, key] of ANCHOR_PCTS) {
    const n = toNum(dist[key])
    if (n !== null) anchors.push({ pct: a, value: n })
  }
  if (anchors.length < 2) return null
  for (let i = 1; i < anchors.length; i++) {
    if (anchors[i].value < anchors[i - 1].value) return null
  }

  const first = anchors[0]
  const last = anchors[anchors.length - 1]
  if (p <= first.pct) return first.value
  if (p >= last.pct) return last.value

  for (let i = 0; i < anchors.length - 1; i++) {
    const lo = anchors[i]
    const hi = anchors[i + 1]
    if (p > lo.pct && p <= hi.pct) {
      const span = hi.pct - lo.pct
      if (span <= 0) return hi.value
      return lo.value + ((p - lo.pct) / span) * (hi.value - lo.value)
    }
  }
  return null
}

/**
 * 노출 위치 — 1페이지 안에서 이 상품이 얼마나 보여지고 있나.
 *
 * ⚠ `calculatedImpressions` 를 쓰면 안 된다. 그건 그 상품이 노출된 **모든 키워드의
 *    합계**라(정본 §7), 여러 키워드로 뜨는 상품이 무조건 상위로 잡힌다.
 *    담기가 대표 키워드 한 개분만 뽑아 `상품_impressions` 로 실어 보낸다.
 * ⚠ 이 키워드로 안 뜬 상품은 `상품_impressions` 가 **0 이 아니라 null** 이다
 *    (`topSourcingProducts` 는 키워드 무관 RPI 상위 3개라 섞인다). null 이면
 *    여기서도 null 을 돌려줘야 「모른다」가 「노출 바닥」으로 둔갑하지 않는다.
 *
 * @param {object} row 보드 행
 * @returns {{pct:number, dist:object}|null}
 */
export function impressionPosition (row) {
  if (!row) return null
  const dist = {
    min: row.minImpressions,
    median: row.medianImpressions,
    max: row.maxImpressions
  }
  const pct = pctIn(row.상품_impressions, dist)
  return pct === null ? null : { pct, dist }
}

/**
 * 기회 라벨을 **읽는다**. 판정하지 않는다(정본 §4).
 *
 * 보드에는 1페이지 전체 분포가 아니라 상위 3행만 오므로 다시 판정할 수 없다.
 * 아는 라벨이 아니면 null — 모르는 문자열에 기본 가중치를 먹여 조용히 점수를
 * 매기느니 「없음」으로 두는 게 낫다.
 *
 * @param {object} row 보드 행
 * @returns {string|null} OPP 의 값 중 하나
 */
export function opportunityOf (row) {
  if (!row) return null
  const label = row.상품_기회
  if (typeof label !== 'string' || label === '') return null
  return Object.prototype.hasOwnProperty.call(OPP_WEIGHT, label) ? label : null
}

/**
 * 진입점수 = 기회 × ROI × 시장파이 (정본 §3.1). 보드 기본 정렬.
 *
 * ```
 * w     = OPP_WEIGHT[기회] ?? 0.45
 * roiK  = min(ROI, 300) / 100          ROI 없으면 1.0 (잠정)
 * pieK  = log10(1 + 시장파이 / 100만)   시장파이 없으면 1.0 (잠정)
 * value = round(w × roiK × pieK × 10, 소수1)
 * ```
 *
 * - 미산출 축을 0 으로 깔면 그 축을 모르는 행이 **전부 0점 동점**이 돼 정렬이
 *   통째로 죽는다. 그래서 두 축 다 「모르면 계수 1.0(중립)」으로 두고 `missing` 에
 *   어느 축인지를 담아 준다 — 화면이 「잠정」 배지와 툴팁에 쓴다.
 *   덕분에 "시장 크고 틈 있는 것부터 1688 찾아라"가 된다.
 * - 시장파이(`월매출`)는 키워드 평균 월매출인데, **키워드소싱분석 담기 경로에만**
 *   실린다. 로켓그로스 소싱관리는 담기가 1688·쿠팡 링크뿐이라 `월매출` 이 아예
 *   없다 — 0 으로 잡으면 그 보드는 모든 행이 0점이 된다.
 * - **진짜 0 과 모름은 가른다.** 월매출이 실제로 `0`(매출이 정말 없는 시장)이면
 *   `log10(1)=0` 이 맞는 답이다. `null`/`undefined`/빈 문자열만 1.0 으로 친다.
 *   정본 §8-④ — 「세어보지 않은 것」과 「0」은 다른 값이다.
 *
 * @param {object} row 보드 행
 * @returns {{value:number, firm:boolean, w:number, roiK:number, pieK:number,
 *            missing:string[]}|null} missing = 미산출 축 이름('roi'|'pie'),
 *            firm = missing 이 비었을 때만 true
 */
export function entryScore (row) {
  if (!row || typeof row !== 'object') return null

  const label = opportunityOf(row)
  const w = label !== null ? OPP_WEIGHT[label] : DEFAULT_OPP_WEIGHT

  const missing = []

  const roi = toNum(row.ROI)
  if (roi === null) missing.push('roi')
  const roiK = roi !== null ? Math.min(roi, ROI_CAP) / 100 : 1

  // 시장파이를 「모른다」는 0 이 아니라 중립 1.0 이다 — 0 을 곱하면 모르는 행이
  // 전부 0점 동점이 돼 정렬이 죽는다. 반대로 **실제로 들어온 0** 은 그대로 쓴다:
  // 매출이 정말 없는 시장이면 log10(1)=0 이 맞는 답이다. toNum 이 null/빈값만
  // null 로 돌려주므로 여기서 두 경우가 갈린다(정본 §8-④).
  const rawPie = toNum(row.월매출)
  if (rawPie === null) missing.push('pie')
  // 음수 매출은 있을 수 없다. 걸러두지 않으면 log10 이 -Infinity/NaN 을 내고
  // 그 NaN 이 점수를 타고 정렬까지 번진다.
  const pieK = rawPie !== null
    ? Math.log10(1 + Math.max(0, rawPie) / MARKET_PIE_LOG_BASE)
    : 1

  const value = Math.round(w * roiK * pieK * SCORE_SCALE * 10) / 10
  return { value, firm: missing.length === 0, w, roiK, pieK, missing }
}

export default {
  OPP,
  OPP_WEIGHT,
  DEFAULT_OPP_WEIGHT,
  ROI_CAP,
  MARKET_PIE_LOG_BASE,
  SCORE_SCALE,
  ONE_PAGE_PRICE_FIELD,
  pctIn,
  valueAtPct,
  rpiPosition,
  pricePosition,
  priceDist,
  impressionPosition,
  opportunityOf,
  entryScore
}
